import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { iPromo, iTicketingSetup } from 'services/Ticketing/Ticketing.type';
import { formatMoney, primaryButton, SwitchField } from '../common';

interface PromoDialogProps {
  promo: Partial<iPromo>;
  data: iTicketingSetup;
  onClose: () => void;
  onSave: (input: Omit<iPromo, 'id' | 'used_count'>, id?: number) => void;
  onDelete: (id: number) => void;
}

export function PromoDialog({
  promo,
  data,
  onClose,
  onSave,
  onDelete,
}: PromoDialogProps): JSX.Element {
  const [v, setV] = useState<Omit<iPromo, 'id' | 'used_count'>>({
    code: promo.code ?? '',
    kind: promo.kind ?? 'percent',
    value: promo.value ?? 10,
    max_uses: promo.max_uses ?? null,
    valid_until: promo.valid_until ?? null,
    ticket_type_ids: promo.ticket_type_ids ?? [],
    is_active: promo.is_active ?? true,
  });

  const paid = data.types.filter((t) => t.price_cents > 0);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {promo.id ? `Code ${promo.code ?? ''}` : 'Nouveau code promo'}
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
          pt: '8px !important',
        }}
      >
        <TextField
          size="small"
          label="Code"
          value={v.code}
          onChange={(e) => setV({ ...v, code: e.target.value.toUpperCase() })}
        />
        <Select
          size="small"
          value={v.kind}
          onChange={(e) =>
            setV({ ...v, kind: e.target.value as iPromo['kind'] })
          }
          label="Type"
        >
          <MenuItem value="percent">Pourcentage</MenuItem>
          <MenuItem value="amount">Montant</MenuItem>
        </Select>
        <TextField
          size="small"
          type="number"
          label={
            v.kind === 'percent'
              ? 'Réduction (%)'
              : `Réduction (centimes de ${data.settings.currency})`
          }
          value={v.value}
          helperText={
            v.kind === 'amount'
              ? formatMoney(v.value, data.settings.currency)
              : undefined
          }
          onChange={(e) => setV({ ...v, value: Number(e.target.value) })}
        />
        <TextField
          size="small"
          type="number"
          label="Utilisations max"
          value={v.max_uses ?? ''}
          onChange={(e) =>
            setV({
              ...v,
              max_uses: e.target.value ? Number(e.target.value) : null,
            })
          }
        />
        <TextField
          size="small"
          type="date"
          label="Valable jusqu'au"
          InputLabelProps={{ shrink: true }}
          value={v.valid_until ?? ''}
          onChange={(e) => setV({ ...v, valid_until: e.target.value || null })}
        />
        <Box sx={{ gridColumn: '1 / -1' }}>
          <SwitchField
            label="Actif"
            checked={v.is_active}
            onChange={(value) => setV({ ...v, is_active: value })}
          />
        </Box>
        {paid.length > 0 && (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography sx={{ fontSize: 13, color: '#666' }}>
              Billets concernés (aucun = tous)
            </Typography>
            {paid.map((t) => (
              <FormControlLabel
                key={t.id}
                label={t.name_fr}
                control={
                  <Checkbox
                    size="small"
                    checked={v.ticket_type_ids.includes(t.id)}
                    onChange={(e) =>
                      setV({
                        ...v,
                        ticket_type_ids: e.target.checked
                          ? [...v.ticket_type_ids, t.id]
                          : v.ticket_type_ids.filter((x) => x !== t.id),
                      })
                    }
                  />
                }
              />
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        {promo.id ? (
          <Button
            color="error"
            sx={{ textTransform: 'none' }}
            onClick={() => {
              if (window.confirm('Supprimer ce code ?'))
                onDelete(promo.id as number);
            }}
          >
            Supprimer
          </Button>
        ) : (
          <span />
        )}
        <Box>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            disabled={!v.code}
            sx={primaryButton}
            onClick={() => onSave(v, promo.id)}
          >
            Enregistrer
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default PromoDialog;
